import React from 'react';
import {
  ConfigProvider,
  Table,
  Transfer,
  TransferProps,
  TableColumnType,
} from 'antd';
import difference from 'lodash/difference';
import { Colors } from 'works/legacy/lib/style-helper';
import { imagePath } from '@common/services/images-service';
import { get } from 'lodash';
import { clsx } from 'clsx';
import { TransferItem } from 'antd/lib/transfer';

const EmptyState = () => (
  <>
    <div className="wk-table-transfer__empty-state">
      <img
        className="wk-table-transfer__empty-image"
        src={imagePath('./empty-table-transfer.gif')}
      ></img>
    </div>
    <style jsx>{`
      .wk-table-transfer__empty-state {
        padding: 120px 0;
        font-size: 16px;
        line-height: 22px;
      }
      .wk-table-transfer__empty-image {
        padding: 20px 0;
      }
      :global(.ant-table-tbody > tr.ant-table-placeholder > td) {
        border: none;
      }
    `}</style>
  </>
);

type DataItem = Omit<TransferItem, 'key'> & {
  selectKey?: string;
  key?: string;
};

type Titles = {
  left?: React.ReactNode;
  right?: React.ReactNode;
  leftSingular?: React.ReactNode;
  rightSingular?: React.ReactNode;
  leftPlural?: React.ReactNode;
  rightPlural?: React.ReactNode;
};

export type TableTransferProps<T> = {
  dataSource: T[];
  columns: TableColumnType<T>[];
  targetKeys: string[];
  className?: string;
  loading?: boolean;
  selectKeyEnabled?: boolean;
  filterOption?: (inputValue: string, option: T) => boolean;
  titles?: Titles;
  headerLarge?: boolean;
} & Omit<TransferProps<T>, 'titles' | 'listStyle' | 'dataSource'>;

export const TableTransfer = <T extends TransferItem>({
  columns,
  selectKeyEnabled = false,
  className = '',
  loading,
  titles = {
    leftSingular: 'Item Available',
    rightSingular: 'Item Selected',
    leftPlural: 'Items Available',
    rightPlural: 'Items Selected',
  },
  filterOption = filterOptionForAllColumns(columns),
  dataSource = [],
  targetKeys = [],
  rowKey = idAsKey,
  headerLarge = false,
  ...restProps
}: TableTransferProps<T>) => {
  const computedTitles = handleTitles(titles, dataSource, targetKeys);

  return (
    <>
      <Transfer
        className={clsx('wk-table-transfer', className, {
          'wk-table-transfer--header-large': headerLarge,
        })}
        titles={computedTitles}
        filterOption={filterOption}
        dataSource={dataSource}
        targetKeys={targetKeys}
        rowKey={rowKey}
        {...restProps}
        locale={{
          itemUnit: '',
          itemsUnit: '',
        }}
      >
        {({
          dataSource,
          filteredItems,
          onItemSelectAll,
          onItemSelect,
          selectedKeys: listSelectedKeys,
          disabled: listDisabled,
        }) => {
          function selectByKey({ key }: TransferItem, selected: boolean) {
            onItemSelect(key!, selected);
          }

          function selectBySelectKey(
            { selectKey }: DataItem,
            selected: boolean
          ) {
            const keys = dataSource
              .filter((item) => item.selectKey === selectKey)
              .map(({ key }) => key!);

            onItemSelectAll(keys, selected);
          }

          function handleSelect(item: TransferItem, selected: boolean) {
            const onSelect = selectKeyEnabled ? selectBySelectKey : selectByKey;
            onSelect(item, selected);
          }

          const rowSelection = {
            getCheckboxProps: (item: TransferItem) => {
              return {
                disabled: listDisabled || item.disabled,
              };
            },
            onSelectAll(selected: boolean, selectedRows: TransferItem[]) {
              const treeSelectedKeys = selectedRows
                .filter((item) => item && !item.disabled)
                .map(({ key }) => key!);
              const diffKeys = selected
                ? difference(treeSelectedKeys, listSelectedKeys)
                : difference(listSelectedKeys, treeSelectedKeys);

              onItemSelectAll(diffKeys, selected);
            },
            onSelect: handleSelect,
            selectedRowKeys: listSelectedKeys,
          };

          return (
            <ConfigProvider renderEmpty={() => <EmptyState />}>
              <Table
                loading={loading}
                rowSelection={rowSelection}
                columns={columns}
                dataSource={filteredItems}
                style={{
                  pointerEvents: listDisabled ? 'none' : undefined,
                }}
                onRow={(item) => ({
                  onClick: () => {
                    if (item.disabled || listDisabled) {
                      return;
                    }

                    handleSelect(item, !listSelectedKeys.includes(item.key!));
                  },
                })}
              />
            </ConfigProvider>
          );
        }}
      </Transfer>
      <style jsx>{`
        :global(
          .wk-table-transfer
            .ant-transfer-list-header
            .ant-checkbox-wrapper
            + span
        ) {
          padding-left: 10px;
        }
        :global(.wk-table-transfer .ant-transfer-list-header-selected) {
          font-weight: bold;
        }
        :global(
          .wk-table-transfer
            .ant-dropdown-trigger.ant-transfer-list-header-dropdown
        ) {
          display: none;
        }
        :global(
          .wk-table-transfer
            .ant-transfer-list-header
            .ant-transfer-list-checkbox
        ) {
          margin-right: 12px;
        }
        :global(.wk-table-transfer .ant-transfer-list-header) {
          padding-left: 8px;
          padding-top: 10px;
        }
        :global(.wk-table-transfer .ant-transfer-list-header-title) {
          position: inherit;
        }
        :global(
          .wk-table-transfer
            .ant-transfer-list-header
            .ant-transfer-list-header-title
        ) {
          text-align: left;
          font-weight: 800;
        }
        :global(
          .wk-table-transfer.ant-transfer-customize-list
            .ant-transfer-list-body-customize-wrapper
        ) {
          padding: 0 0 14px 0;
        }
        :global(
          .wk-table-transfer.ant-transfer-customize-list .ant-transfer-list
        ) {
          width: 50%;
        }
        :global(
          .wk-table-transfer
            .ant-transfer-operation
            .ant-btn-icon-only.ant-btn-sm
        ) {
          width: 32px;
          height: 32px;
        }
        :global(
          .wk-table-transfer .ant-transfer-operation .ant-btn:first-child
        ) {
          margin-bottom: 12px;
        }
        :global(.wk-table-transfer .ant-table-tbody > tr > td) {
          line-height: 27px;
          cursor: pointer;
        }
        :global(
          .wk-table-transfer
            .ant-table-container
            table
            > thead
            > tr:first-child
            th:first-child,
          .wk-table-transfer
            .ant-table-container
            table
            > thead
            > tr:first-child
            th:last-child
        ) {
          border-radius: 0;
        }
        :global(
          .wk-table-transfer.ant-transfer-customize-list
            .ant-table-wrapper
            .ant-table-pagination.ant-pagination
        ) {
          margin-right: 14px;
        }
        :global(
          .wk-table-transfer table tr th.ant-table-selection-column,
          .wk-table-transfer table tr td.ant-table-selection-column
        ) {
          width: 28px;
          min-width: 28px;
          padding-right: 0;
          text-align: left;
        }
        :global(.wk-table-transfer .ant-transfer-list-body-search-wrapper),
        :global(.wk-table-transfer .ant-table-thead > tr > th.ant-table-cell) {
          background-color: ${Colors.steelLightest};
        }
        :global(.wk-table-transfer--header-large .ant-transfer-list-header) {
          height: 52px;
        }
      `}</style>
    </>
  );
};

function idAsKey(record: DataItem) {
  return record.id;
}

function buildCounters(dataSource: DataItem[], targetKeys: string[]) {
  const leftCounter = dataSource.length - targetKeys.length;
  const rightCounter = dataSource.length - leftCounter;

  return [leftCounter, rightCounter];
}

function handleTitles(
  titles: Titles,
  dataSource: DataItem[],
  targetKeys: string[]
) {
  if (titles.left || titles.right) {
    return [titles.left, titles.right];
  }

  const [leftCounter, rightCounter] = buildCounters(dataSource, targetKeys);

  return [
    leftCounter !== 1 ? titles.leftPlural : titles.leftSingular,
    rightCounter !== 1 ? titles.rightPlural : titles.rightSingular,
  ];
}

function filterOptionForColumns<T>(
  columnsKeys: TableColumnType<T>['dataIndex'][]
) {
  return (inputValue: string, option: T) =>
    columnsKeys.some((key) => {
      if (!key) {
        return false;
      }

      return (
        get(option, key)
          .toString()
          .toLowerCase()
          .indexOf(inputValue.toLowerCase()) > -1
      );
    });
}

function filterOptionForAllColumns<T>(columns: TableColumnType<T>[]) {
  return filterOptionForColumns(columns.map((column) => column.dataIndex));
}

TableTransfer.filterOptionForColumns = filterOptionForColumns;
